// Base BLE probe adapter. Concrete vendor adapters extend this class and implement
// at minimum `parseAdvertisement` (for advertising-based sensors) or `connectAndRead`
// (for pair-based GATT sensors).
//
// The adapter relies on @capacitor-community/bluetooth-le. On web it degrades to the
// Web Bluetooth API, so Capacitor's plugin handles both runtimes.

import { BleClient, type ScanResult } from '@capacitor-community/bluetooth-le';
import type {
  BleProbeAdapter,
  DetectedProbe,
  TempReading,
  ProbeVendor,
} from '@trackeat/shared';

export interface AdvertisingReading {
  reading: TempReading;
  probe: DetectedProbe;
}

export interface GenericBleOptions {
  /** Milliseconds between successive reads in the internal map (debounce noisy advertisers). */
  debounceMs: number;
  /** How long a DetectedProbe is kept in the scan result cache after last seen. */
  ttlMs: number;
}

const DEFAULT_OPTIONS: GenericBleOptions = {
  debounceMs: 3_000,
  ttlMs: 60_000,
};

export abstract class GenericBleProbeAdapter implements BleProbeAdapter {
  abstract readonly vendor: ProbeVendor;

  protected readonly options: GenericBleOptions;
  private initialised = false;

  protected readonly lastReadings = new Map<string, AdvertisingReading>();

  constructor(options: Partial<GenericBleOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Parse one advertisement into a reading. Return null if the advertisement
   * does not belong to this vendor / the payload is malformed.
   */
  protected abstract parseAdvertisement(result: ScanResult): AdvertisingReading | null;

  protected async ensureInitialised(): Promise<void> {
    if (this.initialised) return;
    // androidNeverForLocation: false → il plugin chiede ACCESS_FINE_LOCATION
    // oltre a BLUETOOTH_SCAN. Su Android 11- è obbligatorio per ricevere
    // advertisement (senza location il scan torna lista vuota). Su Android 12+
    // dipende dal use-case: per "deriva la posizione dell'utente da BLE
    // beacons" → location obbligatoria; per "scopri device specifico" →
    // facoltativa col flag neverForLocation. Le sonde HACCP sono
    // advertisement-only (lettura temperatura), e su molti device Xiaomi/
    // Huawei senza fine_location il scan non torna risultati. Quindi la
    // chiediamo sempre.
    await BleClient.initialize({ androidNeverForLocation: false });
    this.initialised = true;
  }

  async scan(timeoutMs = 8_000): Promise<DetectedProbe[]> {
    await this.ensureInitialised();
    const seen = new Map<string, DetectedProbe>();
    await BleClient.requestLEScan({ allowDuplicates: true }, (result) => {
      const parsed = this.parseAdvertisement(result);
      if (!parsed) return;
      this.lastReadings.set(parsed.probe.device_id, parsed);
      seen.set(parsed.probe.device_id, parsed.probe);
    });
    await new Promise<void>((r) => setTimeout(r, timeoutMs));
    await BleClient.stopLEScan();
    return Array.from(seen.values());
  }

  // Most vendor probes in MVP scope are advertising-based: "pairing" is a logical
  // association only. Subclasses that need GATT pairing should override.
  async pair(device: DetectedProbe): Promise<{ paired_id: string }> {
    return { paired_id: device.device_id };
  }

  async unpair(_pairedId: string): Promise<void> {
    // no-op for advertising-based probes
  }

  async subscribe(
    pairedId: string,
    onReading: (reading: TempReading) => void,
  ): Promise<() => void> {
    await this.ensureInitialised();
    let active = true;
    await BleClient.requestLEScan({ allowDuplicates: true }, (result) => {
      if (!active) return;
      const parsed = this.parseAdvertisement(result);
      if (!parsed) return;
      if (parsed.probe.device_id !== pairedId) return;
      const prev = this.lastReadings.get(pairedId);
      const now = Date.now();
      const prevTs = prev ? Date.parse(prev.reading.received_at) : 0;
      if (now - prevTs < this.options.debounceMs) return;
      this.lastReadings.set(pairedId, parsed);
      onReading(parsed.reading);
    });
    return async () => {
      active = false;
      try {
        await BleClient.stopLEScan();
      } catch {
        // swallow — scan may already be stopped by another subscriber
      }
    };
  }

  async readOnce(pairedId: string): Promise<TempReading> {
    const cached = this.lastReadings.get(pairedId);
    if (cached && Date.now() - Date.parse(cached.reading.received_at) < this.options.ttlMs) {
      return cached.reading;
    }
    // Fallback: do a short scan and capture the first matching advertisement.
    await this.ensureInitialised();
    const reading = await new Promise<TempReading>((resolve, reject) => {
      const timeout = setTimeout(async () => {
        try {
          await BleClient.stopLEScan();
        } catch {
          /* ignore */
        }
        reject(new Error('probe_read_timeout'));
      }, 10_000);
      BleClient.requestLEScan({ allowDuplicates: true }, (result) => {
        const parsed = this.parseAdvertisement(result);
        if (!parsed) return;
        if (parsed.probe.device_id !== pairedId) return;
        clearTimeout(timeout);
        this.lastReadings.set(pairedId, parsed);
        BleClient.stopLEScan().catch(() => undefined);
        resolve(parsed.reading);
      }).catch(reject);
    });
    return reading;
  }

  // Utility: convert @capacitor-community raw manufacturerData map into a Uint8Array
  // for the company id we care about.
  protected manufacturerBytes(
    result: ScanResult,
    companyId: number,
  ): Uint8Array | null {
    const md = result.manufacturerData;
    if (!md) return null;
    const key = companyId.toString();
    const entry = md[key];
    if (!entry) return null;
    // Capacitor exposes DataView; normalise to Uint8Array.
    const view = entry as unknown as { buffer: ArrayBufferLike; byteOffset: number; byteLength: number };
    return new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
  }

  protected nowIso(): string {
    return new Date().toISOString();
  }
}
